import React, { createContext, useContext, useState, useEffect, useCallback } from "react";
import {
  fetchMyCinema, toggleList, rateMovie,
  authLogin, authRegister, authMe, setAuthToken,
} from "../lib/api";

const MyCinemaContext = createContext(null);
export const useMyCinema = () => useContext(MyCinemaContext);
// Convenience alias for auth-focused consumers.
export const useAuth = () => useContext(MyCinemaContext);

const EMPTY = { watchlist: [], watched: [], favorites: [], ratings: {} };
const TOKEN_KEY = "ic_token";

export const MyCinemaProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [data, setData] = useState(EMPTY);
  const [authOpen, setAuthOpen] = useState(false);
  const [ready, setReady] = useState(false);

  // Hydrate session from a stored token on load.
  useEffect(() => {
    const token = localStorage.getItem(TOKEN_KEY);
    if (!token) { setReady(true); return; }
    setAuthToken(token);
    authMe()
      .then((u) => { setUser(u); return fetchMyCinema(u.user_id).then(setData).catch(() => {}); })
      .catch(() => { localStorage.removeItem(TOKEN_KEY); setAuthToken(null); })
      .finally(() => setReady(true));
  }, []);

  const _establish = useCallback(async ({ token, user: u }) => {
    localStorage.setItem(TOKEN_KEY, token);
    setAuthToken(token);
    setUser(u);
    fetchMyCinema(u.user_id).then(setData).catch(() => {});
    setAuthOpen(false);
    return u;
  }, []);

  const login = useCallback(async (email, password) => {
    return _establish(await authLogin(email, password));
  }, [_establish]);

  const register = useCallback(async (email, password, name) => {
    return _establish(await authRegister(email, password, name));
  }, [_establish]);

  const signOut = useCallback(() => {
    localStorage.removeItem(TOKEN_KEY);
    setAuthToken(null);
    setUser(null);
    setData(EMPTY);
  }, []);

  const isStaff = !!user && (user.role === "admin" || user.role === "editor");
  const isAdmin = !!user && user.role === "admin";

  const requireAuth = useCallback((fn) => (...args) => {
    if (!user) { setAuthOpen(true); return; }
    return fn(...args);
  }, [user]);

  const toggle = useCallback(async (list_name, item_id) => {
    if (!user) { setAuthOpen(true); return; }
    const d = await toggleList(user.user_id, list_name, item_id);
    setData(d);
  }, [user]);

  const rate = useCallback(async (movie_id, score) => {
    if (!user) { setAuthOpen(true); return; }
    const d = await rateMovie(user.user_id, movie_id, score);
    setData(d);
  }, [user]);

  const inList = (list_name, id) => (data?.[list_name] || []).includes(id);

  return (
    <MyCinemaContext.Provider value={{
      user, data, ready, login, register, signOut, toggle, rate, inList,
      authOpen, setAuthOpen, requireAuth, isStaff, isAdmin,
    }}>
      {children}
    </MyCinemaContext.Provider>
  );
};
